import * as React from "react";
import { deactivateCurrentWebPushSubscription } from "../lib/webPush";
import { supabase } from "../lib/supabase";

interface User {
  id: number;
  username: string;
  nickname?: string | null;
  email: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** Supabase 로그인 후 access_token으로 Vercel ensure 호출 및 로그인 상태 전환 */
  loginWithSupabase: (supabaseAccessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

function loadCachedUser(): User | null {
  const cached = localStorage.getItem("user_data");
  if (!cached) return null;
  try {
    return JSON.parse(cached) as User;
  } catch {
    return null;
  }
}

function fallbackUserFromAuthUser(u: { email?: string | null }): User {
  return {
    id: 0,
    username: "",
    email: u.email || "",
  };
}

function persistUserData(user: User) {
  try {
    localStorage.setItem("user_data", JSON.stringify(user));
  } catch {
    // ignore
  }
}

/**
 * Vercel Function POST /api/auth/ensure-supabase-session 호출.
 * Django SimpleJWT를 발급하지 않고, Django user 정보만 반환한다.
 */
async function callVercelEnsure(supabaseAccessToken: string): Promise<User> {
  const res = await fetch("/api/auth/ensure-supabase-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token: supabaseAccessToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "세션 연결에 실패했습니다.");
  }
  const data = await res.json();
  return data.user as User;
}

/**
 * Supabase 세션 토큰을 localStorage의 access_token에 저장.
 * api.ts의 request()가 이 토큰을 읽어 Bearer로 사용하며,
 * Django의 SupabaseJWTAuthentication이 검증한다.
 */
function persistSupabaseToken(session: { access_token: string; refresh_token?: string | null } | null) {
  if (!session) return;
  try {
    localStorage.setItem("access_token", session.access_token);
    if (session.refresh_token) {
      localStorage.setItem("refresh_token", session.refresh_token);
    }
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const initAuth = async () => {
      try {
        let currentUser: User | null = null;

        // 1. Supabase 세션 확인 → Vercel ensure 호출로 Django user 매핑 보장
        if (supabase) {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (session && !error) {
            // Supabase JWT를 access_token으로 저장 (Django API용)
            persistSupabaseToken(session);

            try {
              // Vercel Function으로 Django user 매핑/생성
              currentUser = await callVercelEnsure(session.access_token);
              persistUserData(currentUser);
            } catch (ensureErr) {
              if (import.meta.env.DEV) {
                console.warn("Vercel ensure failed, using fallback:", ensureErr);
              }
              // Vercel도 실패하면 캐시된 user_data 또는 Supabase 프로필 기반 최소 User
              currentUser = loadCachedUser();
              if (!currentUser && session.user) {
                currentUser = fallbackUserFromAuthUser(session.user);
                persistUserData(currentUser);
              }
            }
          }
        }

        setUser(currentUser);
      } catch (e) {
        console.error("Failed during init:", e);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Supabase 인증 상태 변화 감지
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (import.meta.env.DEV) {
          console.log("Supabase Auth Event:", event);
        }

        if (event === "SIGNED_IN") {
          if (session?.access_token) {
            persistSupabaseToken(session);
            try {
              const ensuredUser = await callVercelEnsure(session.access_token);
              setUser(ensuredUser);
              persistUserData(ensuredUser);
            } catch (e) {
              if (import.meta.env.DEV) {
                console.warn("Vercel ensure (SIGNED_IN) failed:", e);
              }
              const cached = loadCachedUser();
              if (cached) {
                setUser(cached);
              } else if (session.user) {
                const fb = fallbackUserFromAuthUser(session.user);
                setUser(fb);
                persistUserData(fb);
              }
            }
          }
        } else if (event === "TOKEN_REFRESHED") {
          // Supabase가 자동으로 토큰을 갱신함 → localStorage만 업데이트
          if (session) {
            persistSupabaseToken(session);
          }
        } else if (event === "SIGNED_OUT") {
          try {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("user_data");
          } catch {
            // ignore
          }
          setUser(null);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, []);

  React.useEffect(() => {
    const onProfileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ id: number; nickname: string | null }>).detail;
      setUser((current) => {
        if (!current || current.id !== detail.id) return current;
        const updated = { ...current, nickname: detail.nickname };
        persistUserData(updated);
        return updated;
      });
    };
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  /** Supabase 세션 기반 로그인 (Login 페이지에서 호출) */
  const loginWithSupabase = async (supabaseAccessToken: string) => {
    // Supabase 세션 토큰 저장
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      persistSupabaseToken(session);
    }

    try {
      const ensuredUser = await callVercelEnsure(supabaseAccessToken);
      persistUserData(ensuredUser);
      setUser(ensuredUser);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("loginWithSupabase (Vercel ensure) failed:", error);
      }

      // 폴백: 캐시 또는 Supabase 프로필 기반
      const cached = loadCachedUser();
      if (cached) {
        setUser(cached);
      } else if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const fb = fallbackUserFromAuthUser(session.user);
          setUser(fb);
          persistUserData(fb);
        }
      }
    }
  };

  const logout = async () => {
    // Unsubscribe while the user token still authorizes the server-side deactivation.
    await deactivateCurrentWebPushSubscription().catch(() => {});
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_data");
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginWithSupabase,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
